import { Navigate, type RouteObject } from 'react-router';
import PrivateRoute from '../components/PrivateRoute';
import { loadLazyRouteDefault } from '../features/chunkErrorHandler';
import { TrackPage } from '../providers/Tracking/TrackingProvider';
import { PageName } from '../types/Events';

const COMMERCIAL_EMBED_ROUTES: RouteObject[] = [
    {
        path: '/embed',
        handle: { hideAILauncher: true },
        lazy: async () => {
            const EmbeddedApp = await loadLazyRouteDefault(
                './features/embed/EmbeddedApp',
                () => import('./features/embed/EmbeddedApp'),
            );
            return { Component: EmbeddedApp };
        },
        children: [
            {
                path: '/embed/:projectUuid',
                lazy: async () => {
                    const EmbedDashboard = await loadLazyRouteDefault(
                        './pages/EmbedDashboard',
                        () => import('./pages/EmbedDashboard'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.EMBED_DASHBOARD}>
                                <EmbedDashboard />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: '/embed/:projectUuid/tabs/:tabUuid',
                lazy: async () => {
                    const EmbedDashboard = await loadLazyRouteDefault(
                        './pages/EmbedDashboard',
                        () => import('./pages/EmbedDashboard'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.EMBED_DASHBOARD}>
                                <EmbedDashboard />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: '/embed/:projectUuid/chart/:chartUuid',
                lazy: async () => {
                    const EmbedChart = await loadLazyRouteDefault(
                        './pages/EmbedChart',
                        () => import('./pages/EmbedChart'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.EMBED_SAVED_CHART}>
                                <EmbedChart />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: '/embed/:projectUuid/app/:appUuid',
                lazy: async () => {
                    const EmbedApp = await loadLazyRouteDefault(
                        './pages/EmbedApp',
                        () => import('./pages/EmbedApp'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.EMBED_DATA_APP}>
                                <EmbedApp />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: '/embed/:projectUuid/metrics',
                lazy: async () => {
                    const { default: MetricsCatalog } =
                        await import('../pages/MetricsCatalog');
                    return {
                        Component: () => (
                            <TrackPage name={PageName.METRICS_CATALOG}>
                                <MetricsCatalog />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: '/embed/:projectUuid/explore/:exploreId',
                lazy: async () => {
                    const EmbedExplore = await loadLazyRouteDefault(
                        './pages/EmbedExplore',
                        () => import('./pages/EmbedExplore'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.EMBED_EXPLORE}>
                                <EmbedExplore />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: '/embed/:projectUuid/ai-agents/not-authorized',
                lazy: async () => {
                    const AiAgentsNotAuthorizedPage =
                        await loadLazyRouteDefault(
                            './pages/AiAgents/AiAgentsNotAuthorizedPage',
                            () =>
                                import('./pages/AiAgents/AiAgentsNotAuthorizedPage'),
                        );
                    return { Component: AiAgentsNotAuthorizedPage };
                },
            },
            {
                path: '/embed/:projectUuid/ai-agents/:agentUuid',
                lazy: async () => {
                    const AgentPage = await loadLazyRouteDefault(
                        './pages/AiAgents/AgentPage',
                        () => import('./pages/AiAgents/AgentPage'),
                    );
                    return { Component: AgentPage };
                },
                children: [
                    {
                        index: true,
                        element: <Navigate to="threads" replace />,
                    },
                    {
                        path: 'threads',
                        children: [
                            {
                                index: true,
                                lazy: async () => {
                                    const AiAgentNewThreadPage =
                                        await loadLazyRouteDefault(
                                            './pages/AiAgents/AiAgentNewThreadPage',
                                            () =>
                                                import('./pages/AiAgents/AiAgentNewThreadPage'),
                                        );
                                    return {
                                        Component: AiAgentNewThreadPage,
                                    };
                                },
                            },
                            {
                                path: ':threadUuid/messages/:promptUuid/debug',
                                lazy: async () => {
                                    const AiAgentThreadPage =
                                        await loadLazyRouteDefault(
                                            './pages/AiAgents/AgentThreadPage',
                                            () =>
                                                import('./pages/AiAgents/AgentThreadPage'),
                                        );
                                    return {
                                        Component: () => (
                                            <AiAgentThreadPage debug />
                                        ),
                                    };
                                },
                            },
                            {
                                path: ':threadUuid/messages/:promptUuid',
                                lazy: async () => {
                                    const AiAgentThreadPage =
                                        await loadLazyRouteDefault(
                                            './pages/AiAgents/AgentThreadPage',
                                            () =>
                                                import('./pages/AiAgents/AgentThreadPage'),
                                        );
                                    return { Component: AiAgentThreadPage };
                                },
                            },
                            {
                                path: ':threadUuid',
                                lazy: async () => {
                                    const AiAgentThreadPage =
                                        await loadLazyRouteDefault(
                                            './pages/AiAgents/AgentThreadPage',
                                            () =>
                                                import('./pages/AiAgents/AgentThreadPage'),
                                        );
                                    return { Component: AiAgentThreadPage };
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
    // DL Fork: Full UI embedding - allows access to the full BI UI via JWT
    // token. URL format: /embed/:projectUuid/fullui/...#token
    {
        path: '/embed/:projectUuid/fullui',
        handle: { hideAILauncher: true },
        lazy: async () => {
            const EmbedFullUIApp = await loadLazyRouteDefault(
                './features/embed/EmbedFullUIApp',
                () => import('./features/embed/EmbedFullUIApp'),
            );
            return { Component: EmbedFullUIApp };
        },
        children: [
            {
                index: true,
                lazy: async () => {
                    const Home = await loadLazyRouteDefault(
                        '../pages/Home',
                        () => import('../pages/Home'),
                    );
                    return { Component: Home };
                },
            },
            {
                path: 'tables',
                lazy: async () => {
                    const Explorer = await loadLazyRouteDefault(
                        '../pages/Explorer',
                        () => import('../pages/Explorer'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.EXPLORER}>
                                <Explorer />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: 'tables/:tableId',
                lazy: async () => {
                    const Explorer = await loadLazyRouteDefault(
                        '../pages/Explorer',
                        () => import('../pages/Explorer'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.EXPLORER}>
                                <Explorer />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: 'dashboards',
                lazy: async () => {
                    const SavedDashboards = await loadLazyRouteDefault(
                        '../pages/SavedDashboards',
                        () => import('../pages/SavedDashboards'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.SAVED_DASHBOARDS}>
                                <SavedDashboards />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: 'saved',
                lazy: async () => {
                    const SavedQueries = await loadLazyRouteDefault(
                        '../pages/SavedQueries',
                        () => import('../pages/SavedQueries'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.SAVED_QUERIES}>
                                <SavedQueries />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: 'sqlRunner',
                lazy: async () => {
                    const SqlRunner = await loadLazyRouteDefault(
                        '../pages/SqlRunner',
                        () => import('../pages/SqlRunner'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.SQL_RUNNER}>
                                <SqlRunner />
                            </TrackPage>
                        ),
                    };
                },
            },
            // Individual dashboard view routes
            {
                path: 'dashboards/:dashboardUuid',
                lazy: async () => {
                    const Dashboard = await loadLazyRouteDefault(
                        '../pages/Dashboard',
                        () => import('../pages/Dashboard'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.DASHBOARD}>
                                <Dashboard />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: 'dashboards/:dashboardUuid/:mode',
                lazy: async () => {
                    const Dashboard = await loadLazyRouteDefault(
                        '../pages/Dashboard',
                        () => import('../pages/Dashboard'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.DASHBOARD}>
                                <Dashboard />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: 'dashboards/:dashboardUuid/:mode/tabs/:tabUuid',
                lazy: async () => {
                    const Dashboard = await loadLazyRouteDefault(
                        '../pages/Dashboard',
                        () => import('../pages/Dashboard'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.DASHBOARD}>
                                <Dashboard />
                            </TrackPage>
                        ),
                    };
                },
            },
            // Individual saved chart view routes
            {
                path: 'saved/:savedQueryUuid',
                lazy: async () => {
                    const SavedExplorer = await loadLazyRouteDefault(
                        '../pages/SavedExplorer',
                        () => import('../pages/SavedExplorer'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.SAVED_QUERY_EXPLORER}>
                                <SavedExplorer />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: 'saved/:savedQueryUuid/:mode',
                lazy: async () => {
                    const SavedExplorer = await loadLazyRouteDefault(
                        '../pages/SavedExplorer',
                        () => import('../pages/SavedExplorer'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.SAVED_QUERY_EXPLORER}>
                                <SavedExplorer />
                            </TrackPage>
                        ),
                    };
                },
            },
            {
                path: 'saved/:savedQueryUuid/history',
                lazy: async () => {
                    const ChartHistory = await loadLazyRouteDefault(
                        '../pages/ChartHistory',
                        () => import('../pages/ChartHistory'),
                    );
                    return {
                        Component: () => (
                            <TrackPage name={PageName.CHART_HISTORY}>
                                <ChartHistory />
                            </TrackPage>
                        ),
                    };
                },
            },
        ],
    },
];

const COMMERCIAL_AI_AGENTS_ROUTES: RouteObject[] = [
    {
        path: '/ai-agents/admin',
        element: <Navigate to="/generalSettings/ai/general" replace />,
    },
    {
        path: '/ai-agents/admin/threads',
        element: <Navigate to="/generalSettings/ai/threads" replace />,
    },
    {
        path: '/ai-agents/admin/agents',
        element: <Navigate to="/generalSettings/ai/agents" replace />,
    },
    {
        path: '/ai-agents/admin/memories',
        element: <Navigate to="/generalSettings/ai/memories" replace />,
    },
    {
        path: '/ai-agents/admin/reviews',
        element: <Navigate to="/generalSettings/ai/issues" replace />,
    },
    {
        path: '/ai-agents/admin/issues',
        element: <Navigate to="/generalSettings/ai/issues" replace />,
    },
    {
        path: '/ai-agents/',
        lazy: async () => {
            const AgentsRedirect = await loadLazyRouteDefault(
                './pages/AiAgents/AgentsRedirect',
                () => import('./pages/AiAgents/AgentsRedirect'),
            );
            return {
                Component: () => (
                    <PrivateRoute>
                        <AgentsRedirect />
                    </PrivateRoute>
                ),
            };
        },
    },
    {
        path: '/projects/:projectUuid/ai-agents',
        handle: { hideAILauncher: true },
        lazy: async () => {
            const AiAgentsRootLayout = await loadLazyRouteDefault(
                './pages/AiAgents/AiAgentsRootLayout',
                () => import('./pages/AiAgents/AiAgentsRootLayout'),
            );
            return {
                Component: () => (
                    <PrivateRoute>
                        <AiAgentsRootLayout />
                    </PrivateRoute>
                ),
            };
        },
        children: [
            {
                index: true,
                lazy: async () => {
                    const AgentsWelcome = await loadLazyRouteDefault(
                        './pages/AiAgents/AgentsWelcome',
                        () => import('./pages/AiAgents/AgentsWelcome'),
                    );
                    return { Component: AgentsWelcome };
                },
            },
            {
                path: 'not-authorized',
                lazy: async () => {
                    const AiAgentsNotAuthorizedPage =
                        await loadLazyRouteDefault(
                            './pages/AiAgents/AiAgentsNotAuthorizedPage',
                            () =>
                                import('./pages/AiAgents/AiAgentsNotAuthorizedPage'),
                        );
                    return { Component: AiAgentsNotAuthorizedPage };
                },
            },
            {
                path: 'new',
                lazy: async () => {
                    const ProjectAiAgentEditPage = await loadLazyRouteDefault(
                        './pages/AiAgents/ProjectAiAgentEditPage',
                        () => import('./pages/AiAgents/ProjectAiAgentEditPage'),
                    );
                    return {
                        Component: () => (
                            <ProjectAiAgentEditPage isCreateMode />
                        ),
                    };
                },
            },
            {
                path: 'share/:aiThreadShareUuid',
                lazy: async () => {
                    const AiAgentThreadSharePage = await loadLazyRouteDefault(
                        './pages/AiAgents/AiAgentThreadSharePage',
                        () => import('./pages/AiAgents/AiAgentThreadSharePage'),
                    );
                    return { Component: AiAgentThreadSharePage };
                },
            },
            {
                path: 'memories/:slug',
                lazy: async () => {
                    const AiAgentMemoryPage = await loadLazyRouteDefault(
                        './pages/AiAgents/AiAgentMemoryPage',
                        () => import('./pages/AiAgents/AiAgentMemoryPage'),
                    );
                    return { Component: AiAgentMemoryPage };
                },
            },
            {
                path: 'deep-research/:runUuid',
                lazy: async () => {
                    const DeepResearchReportPage = await loadLazyRouteDefault(
                        './pages/AiAgents/DeepResearchReportPage',
                        () => import('./pages/AiAgents/DeepResearchReportPage'),
                    );
                    return { Component: DeepResearchReportPage };
                },
            },
            {
                path: ':agentUuid/memories/:slug',
                lazy: async () => {
                    const AiAgentMemoryPage = await loadLazyRouteDefault(
                        './pages/AiAgents/AiAgentMemoryPage',
                        () => import('./pages/AiAgents/AiAgentMemoryPage'),
                    );
                    return { Component: AiAgentMemoryPage };
                },
            },
            {
                path: ':agentUuid/edit',
                lazy: async () => {
                    const ProjectAiAgentEditPage = await loadLazyRouteDefault(
                        './pages/AiAgents/ProjectAiAgentEditPage',
                        () => import('./pages/AiAgents/ProjectAiAgentEditPage'),
                    );
                    return { Component: ProjectAiAgentEditPage };
                },
                children: [
                    {
                        path: 'evals',
                        lazy: async () => {
                            const ProjectAiAgentEditPage =
                                await loadLazyRouteDefault(
                                    './pages/AiAgents/ProjectAiAgentEditPage',
                                    () =>
                                        import('./pages/AiAgents/ProjectAiAgentEditPage'),
                                );
                            return { Component: ProjectAiAgentEditPage };
                        },
                    },
                    {
                        path: 'evals/:evalUuid',
                        lazy: async () => {
                            const ProjectAiAgentEditPage =
                                await loadLazyRouteDefault(
                                    './pages/AiAgents/ProjectAiAgentEditPage',
                                    () =>
                                        import('./pages/AiAgents/ProjectAiAgentEditPage'),
                                );
                            return { Component: ProjectAiAgentEditPage };
                        },
                    },
                    {
                        path: 'evals/:evalUuid/run/:runUuid',
                        lazy: async () => {
                            const ProjectAiAgentEditPage =
                                await loadLazyRouteDefault(
                                    './pages/AiAgents/ProjectAiAgentEditPage',
                                    () =>
                                        import('./pages/AiAgents/ProjectAiAgentEditPage'),
                                );
                            return { Component: ProjectAiAgentEditPage };
                        },
                    },
                    {
                        path: 'verified-artifacts',
                        lazy: async () => {
                            const ProjectAiAgentEditPage =
                                await loadLazyRouteDefault(
                                    './pages/AiAgents/ProjectAiAgentEditPage',
                                    () =>
                                        import('./pages/AiAgents/ProjectAiAgentEditPage'),
                                );
                            return { Component: ProjectAiAgentEditPage };
                        },
                    },
                    {
                        path: 'verified-artifacts/:artifactUuid',
                        lazy: async () => {
                            const ProjectAiAgentEditPage =
                                await loadLazyRouteDefault(
                                    './pages/AiAgents/ProjectAiAgentEditPage',
                                    () =>
                                        import('./pages/AiAgents/ProjectAiAgentEditPage'),
                                );
                            return { Component: ProjectAiAgentEditPage };
                        },
                    },
                ],
            },
            {
                path: ':agentUuid',
                lazy: async () => {
                    const AgentPage = await loadLazyRouteDefault(
                        './pages/AiAgents/AgentPage',
                        () => import('./pages/AiAgents/AgentPage'),
                    );
                    return { Component: AgentPage };
                },
                children: [
                    {
                        index: true,
                        element: <Navigate to="threads" replace />,
                    },
                    {
                        path: 'threads',
                        children: [
                            {
                                index: true,
                                lazy: async () => {
                                    const AiAgentNewThreadPage =
                                        await loadLazyRouteDefault(
                                            './pages/AiAgents/AiAgentNewThreadPage',
                                            () =>
                                                import('./pages/AiAgents/AiAgentNewThreadPage'),
                                        );
                                    return {
                                        Component: AiAgentNewThreadPage,
                                    };
                                },
                            },
                            {
                                path: ':threadUuid/messages/:promptUuid/debug',
                                lazy: async () => {
                                    const AiAgentThreadPage =
                                        await loadLazyRouteDefault(
                                            './pages/AiAgents/AgentThreadPage',
                                            () =>
                                                import('./pages/AiAgents/AgentThreadPage'),
                                        );
                                    return {
                                        Component: () => (
                                            <AiAgentThreadPage debug />
                                        ),
                                    };
                                },
                            },
                            {
                                path: ':threadUuid/messages/:promptUuid',
                                lazy: async () => {
                                    const AiAgentThreadPage =
                                        await loadLazyRouteDefault(
                                            './pages/AiAgents/AgentThreadPage',
                                            () =>
                                                import('./pages/AiAgents/AgentThreadPage'),
                                        );
                                    return { Component: AiAgentThreadPage };
                                },
                            },
                            {
                                path: ':threadUuid',
                                lazy: async () => {
                                    const AiAgentThreadPage =
                                        await loadLazyRouteDefault(
                                            './pages/AiAgents/AgentThreadPage',
                                            () =>
                                                import('./pages/AiAgents/AgentThreadPage'),
                                        );
                                    return { Component: AiAgentThreadPage };
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

const COMMERCIAL_SLACK_AUTH_ROUTES: RouteObject[] = [
    {
        path: '/auth/slack/success',
        lazy: async () => {
            const SlackAuthSuccess = await loadLazyRouteDefault(
                './pages/SlackAuthSuccess',
                () => import('./pages/SlackAuthSuccess'),
            );
            return { Component: SlackAuthSuccess };
        },
    },
];

export const CommercialWebAppRoutes = [
    ...COMMERCIAL_EMBED_ROUTES,
    ...COMMERCIAL_AI_AGENTS_ROUTES,
    ...COMMERCIAL_SLACK_AUTH_ROUTES,
];

export const CommercialMobileRoutes = [
    ...COMMERCIAL_EMBED_ROUTES,
    ...COMMERCIAL_AI_AGENTS_ROUTES,
];
